import { skipToken } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import isNil from '../../../core/helpers/is-nil';
import { useArticlesBatchQuery } from '../../../modules/article/queries';
import { useLastVisitedArticles } from '../../../modules/last-visited-articles/hooks/use-last-visited-articles';
import { ProductCardSectionLayout } from '../../../visual-components/product-card-section-layout/product-card-section-layout';
import { ProductCard } from '../../../visual-components/product-card/product-card';
import { Spacer } from '../../../visual-components/spacer/spacer';
import { LoadingSpinner } from '../../../visual-components/loading-spinner/loading-spinner';
import { Divider } from '../../../visual-components/divider/divider';
import { GridContainer, GridItem } from '../../../visual-components/grid/grid';

export const LastVisitedArticles: React.FC = () => {
  const { lastVisitedArticles } = useLastVisitedArticles();
  const showLastVisitedArticles = !isNil(lastVisitedArticles) && lastVisitedArticles.length > 0;
  const {
    data = { items: [] },
    error,
    isLoading,
  } = useArticlesBatchQuery(lastVisitedArticles, showLastVisitedArticles ? { keepPreviousData: true } : skipToken);

  const { t } = useTranslation();

  if (!showLastVisitedArticles) {
    return null;
  }

  const loadingState = isLoading && data?.items?.length === 0;

  return (
    <>
      <GridContainer>
        <GridItem>
          <Spacer size="10" />
          <Divider />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem>
          <ProductCardSectionLayout error={error} sectionHeader={t('global.lastVisitedArticles')} variant="carousel">
            {loadingState ? (
              <LoadingSpinner />
            ) : (
              <>
                {data.items
                  // Reverse the order of the last visited articles to show the most recent ones first
                  .toReversed()
                  .map(
                    ({
                      id,
                      itemDesc2,
                      itemDesc3,
                      gloss,
                      surface,
                      effect,
                      bonding,
                      hexCode,
                      series: { name, number, numberColor },
                      weatherResistance,
                      hasRendering,
                    }) => (
                      <ProductCard
                        key={id}
                        articleId={id}
                        bonding={bonding ?? undefined}
                        effect={effect ?? undefined}
                        gloss={gloss ?? undefined}
                        hasRendering={hasRendering}
                        hexCode={hexCode ?? undefined}
                        itemDesc2={itemDesc2}
                        itemDesc3={itemDesc3}
                        series={name}
                        seriesNumber={number}
                        seriesNumberColor={numberColor ?? undefined}
                        surface={surface ?? undefined}
                        title={id}
                        weatherResistance={weatherResistance ?? undefined}
                      />
                    ),
                  )}
              </>
            )}
          </ProductCardSectionLayout>
        </GridItem>
      </GridContainer>
    </>
  );
};
